// src/components/strumenti/DistintaCaviSezione.js
// Problema diverso da "Ordine Cavi Fornitore": una Formazione tecnica scritta come testo
// libero (es. "3×(1×150) + 1×70N + 1×70PE") dice tutto a chi progetta ma nulla a chi acquista,
// e farla interpretare da un algoritmo significa sempre indovinare qualcosa. Qui si evita del
// tutto il problema: le formazioni si definiscono a monte in colonne separate — quantità,
// sezione, etichetta — non come testo. Ogni numero nella distinta finale viene da un campo
// strutturato compilato di proposito, mai da un testo interpretato.
// Strumento a parte, separato da Ordine Cavi Fornitore.
import React, { useState } from "react";
import { Plus, Trash2, Printer, RefreshCw, AlertTriangle } from "lucide-react";

const inp = { width:"100%", padding:"6px 8px", border:"1px solid #e5e7eb", borderRadius:6, fontSize:12, fontFamily:"'Inter',sans-serif", outline:"none", boxSizing:"border-box" };
const sel = { ...inp };

let contatoreId = 1;
function nuovoId() { return contatoreId++; }

function nuovaFormazione() { return { id: nuovoId(), nome:"", componenti:[{ conteggio:"1", sezione:"", etichetta:"" }] }; }
function nuovaRiga() { return { id: nuovoId(), formazioneId:"", lunghezza:"", collegamenti:"1" }; }

export default function DistintaCaviSezione() {
  const [riferimento, setRiferimento] = useState("");
  const [formazioni, setFormazioni] = useState([nuovaFormazione()]);
  const [righe, setRighe] = useState([nuovaRiga()]);

  // --- Formazioni ---
  const aggiungiFormazione = () => setFormazioni(f => [...f, nuovaFormazione()]);
  const eliminaFormazione = (id) => {
    setFormazioni(f => f.filter(x => x.id !== id));
    setRighe(r => r.map(row => row.formazioneId === id ? { ...row, formazioneId:"" } : row));
  };
  const rinominaFormazione = (id, nome) => setFormazioni(f => f.map(x => x.id===id ? {...x, nome} : x));
  const aggiungiComponente = (formazioneId) => setFormazioni(f => f.map(x => x.id===formazioneId ? {...x, componenti:[...x.componenti, { conteggio:"1", sezione:"", etichetta:"" }]} : x));
  const eliminaComponente = (formazioneId, i) => setFormazioni(f => f.map(x => x.id===formazioneId ? {...x, componenti: x.componenti.filter((_,idx)=>idx!==i)} : x));
  const aggiornaComponente = (formazioneId, i, key, val) => setFormazioni(f => f.map(x => x.id===formazioneId ? {...x, componenti: x.componenti.map((c,idx)=>idx===i?{...c,[key]:val}:c)} : x));

  // --- Collegamenti ---
  const aggiungiRiga = () => setRighe(r => [...r, nuovaRiga()]);
  const eliminaRiga = (id) => setRighe(r => r.filter(x => x.id !== id));
  const aggiornaRiga = (id, key, val) => setRighe(r => r.map(x => x.id===id ? {...x,[key]:val} : x));

  const azzeraTutto = () => { setFormazioni([nuovaFormazione()]); setRighe([nuovaRiga()]); setRiferimento(""); };

  const formazioniValide = formazioni.filter(f => f.nome.trim() && f.componenti.some(c => c.sezione.toString().trim() && parseInt(c.conteggio,10)>0));
  const righeSenzaFormazione = righe.filter(r => r.lunghezza && !r.formazioneId);

  // --- Distinta finale — solo aritmetica su campi strutturati, nessuna interpretazione ---
  const distintaMappa = {};
  righe.forEach(r => {
    const f = formazioni.find(x => x.id === Number(r.formazioneId));
    if (!f) return;
    const lunghezza = parseFloat((r.lunghezza||"").toString().replace(",","."));
    const nColl = parseInt(r.collegamenti,10) || 0;
    if (!(lunghezza > 0) || nColl <= 0) return;
    f.componenti.forEach(c => {
      const conteggio = parseInt(c.conteggio,10) || 0;
      const sezione = c.sezione.toString().trim();
      if (!(conteggio > 0) || !sezione) return;
      const chiave = `${sezione}|${c.etichetta.trim().toUpperCase()}`;
      if (!distintaMappa[chiave]) distintaMappa[chiave] = { sezione, etichetta:c.etichetta.trim(), metri:0 };
      distintaMappa[chiave].metri += conteggio * nColl * lunghezza;
    });
  });
  const distinta = Object.values(distintaMappa).sort((a,b) => parseFloat(a.sezione)-parseFloat(b.sezione));
  const totaleMetri = distinta.reduce((s,d)=>s+d.metri,0);

  const stampaPDF = () => {
    const win = window.open("", "_blank");
    const oggi = new Date().toLocaleDateString("it-IT", { day:"numeric", month:"long", year:"numeric" });
    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Distinta Cavi per Sezione — ${riferimento}</title>
    <style>
      *{margin:0;padding:0;box-sizing:border-box;}
      body{font-family:Arial,sans-serif;color:#111;padding:14mm;font-size:12px;}
      @media screen{body{max-width:800px;margin:0 auto;background:#fff;box-shadow:0 2px 16px rgba(0,0,0,.08);}}
      @page{size:A4;margin:14mm;}
      h1{font-size:18px;color:#7c3aed;margin-bottom:2px;}
      .sub{font-size:11px;color:#6b7280;margin-bottom:16px;}
      table{width:100%;border-collapse:collapse;margin-bottom:14px;}
      td,th{border:1px solid #e5e7eb;padding:7px 10px;text-align:left;font-size:12px;}
      th{background:#f9fafb;color:#374151;}
      .totale{background:#faf5ff;border:1px solid #e9d5ff;border-radius:8px;padding:10px 14px;font-size:13px;font-weight:700;color:#6d28d9;}
    </style></head><body>
      <h1>🔀 Distinta Cavi per Sezione</h1>
      <div class="sub">${riferimento ? `Riferimento: ${riferimento} · ` : ""}Generato il ${oggi} con Nexiora — Strumenti Tecnici</div>
      <table>
        <tr><th>Sezione (mm²)</th><th>Etichetta</th><th>Metri da ordinare</th></tr>
        ${distinta.map(d => `<tr><td>${d.sezione}</td><td>${d.etichetta||"—"}</td><td>${d.metri.toLocaleString("it-IT",{maximumFractionDigits:1})} m</td></tr>`).join("")}
      </table>
      <div class="totale">Totale metratura: ${totaleMetri.toLocaleString("it-IT",{maximumFractionDigits:1})} m</div>
    </body></html>`);
    win.document.close();
    setTimeout(() => {
      if (win.closed) return;
      const style = win.document.createElement("style");
      style.textContent = "@media print { .no-print-btn { display:none !important; } }";
      win.document.head.appendChild(style);
      const btn = win.document.createElement("button");
      btn.textContent = "🖨️ Stampa";
      btn.className = "no-print-btn";
      btn.onclick = () => win.print();
      btn.style.cssText = "position:fixed;top:16px;right:16px;z-index:9999;padding:10px 20px;background:#7c3aed;color:#fff;border:none;border-radius:8px;font-size:14px;font-weight:700;cursor:pointer;font-family:Arial,sans-serif;box-shadow:0 4px 12px rgba(0,0,0,.2);";
      win.document.body.appendChild(btn);
    }, 50);
  };

  return (
    <div>
      <div style={{ background:"#faf5ff", border:"1px solid #e9d5ff", borderRadius:8, padding:"8px 12px", marginBottom:14, fontSize:11, color:"#6d28d9" }}>
        💜 Definisci prima le formazioni che usi, componente per componente (quantità, sezione, etichetta) — non come testo. Poi, nell'elenco collegamenti, si sceglie la formazione da un menu invece di riscriverla: ogni numero nella distinta finale viene da un campo compilato di proposito, mai da un testo interpretato.
      </div>

      <div style={{ marginBottom:16 }}>
        <label style={{ fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 }}>Riferimento (facoltativo, usato in stampa)</label>
        <input value={riferimento} onChange={e=>setRiferimento(e.target.value)} style={{...inp, maxWidth:320}} placeholder="Es. Commessa 55-26"/>
      </div>

      <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:10 }}>1 · Formazioni</div>
      {formazioni.map(f => (
        <div key={f.id} style={{ border:"1px solid #e5e7eb", borderRadius:8, padding:12, marginBottom:10 }}>
          <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:10 }}>
            <input value={f.nome} onChange={e=>rinominaFormazione(f.id, e.target.value)} style={{...inp, maxWidth:260, fontWeight:700}} placeholder="Nome formazione — es. Dorsale principale"/>
            <button onClick={()=>eliminaFormazione(f.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"#dc2626", padding:2, marginLeft:"auto" }}><Trash2 size={14}/></button>
          </div>
          <div style={{ fontSize:10.5, color:"#9ca3af", marginBottom:6, display:"grid", gridTemplateColumns:"60px 20px 90px 1fr 24px", gap:6 }}>
            <span>Quantità</span><span></span><span>Sezione (mm²)</span><span>Etichetta (facoltativa: N, PE, fase...)</span><span></span>
          </div>
          {f.componenti.map((c,i) => (
            <div key={i} style={{ display:"grid", gridTemplateColumns:"60px 20px 90px 1fr 24px", gap:6, marginBottom:5, alignItems:"center" }}>
              <input value={c.conteggio} onChange={e=>aggiornaComponente(f.id,i,"conteggio",e.target.value)} style={inp} placeholder="N°"/>
              <span style={{ textAlign:"center", color:"#9ca3af" }}>×</span>
              <input value={c.sezione} onChange={e=>aggiornaComponente(f.id,i,"sezione",e.target.value)} style={inp} placeholder="mm²"/>
              <input value={c.etichetta} onChange={e=>aggiornaComponente(f.id,i,"etichetta",e.target.value)} style={inp} placeholder="Es. N, PE, fase R..."/>
              <button onClick={()=>eliminaComponente(f.id,i)} style={{ background:"none", border:"none", cursor:"pointer", color:"#dc2626", padding:2 }}><Trash2 size={12}/></button>
            </div>
          ))}
          <button onClick={()=>aggiungiComponente(f.id)} style={{ display:"flex", alignItems:"center", gap:4, fontSize:11, fontWeight:700, color:"#7c3aed", background:"none", border:"none", cursor:"pointer", marginTop:4 }}>
            <Plus size={11}/> Componente
          </button>
        </div>
      ))}
      <button onClick={aggiungiFormazione} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11.5, fontWeight:700, color:"#7c3aed", background:"none", border:"none", cursor:"pointer", marginBottom:22 }}>
        <Plus size={13}/> Nuova formazione
      </button>

      <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:10 }}>2 · Collegamenti</div>
      <div style={{ overflowX:"auto", border:"1px solid #e5e7eb", borderRadius:8, marginBottom:8 }}>
        <table style={{ borderCollapse:"collapse", fontSize:12, width:"100%", minWidth:520 }}>
          <thead>
            <tr style={{ background:"#f9fafb" }}>
              <th style={{ padding:"6px 8px", textAlign:"left", color:"#6b7280", minWidth:220 }}>Formazione</th>
              <th style={{ padding:"6px 8px", textAlign:"left", color:"#6b7280", minWidth:110 }}>Lunghezza per collegamento (m)</th>
              <th style={{ padding:"6px 8px", textAlign:"left", color:"#6b7280", minWidth:100 }}>N. Collegamenti</th>
              <th style={{ width:32 }}></th>
            </tr>
          </thead>
          <tbody>
            {righe.map(r => (
              <tr key={r.id}>
                <td style={{ padding:4 }}>
                  <select value={r.formazioneId} onChange={e=>aggiornaRiga(r.id,"formazioneId",e.target.value)} style={sel}>
                    <option value="">— Seleziona formazione —</option>
                    {formazioniValide.map(f => <option key={f.id} value={f.id}>{f.nome}</option>)}
                  </select>
                </td>
                <td style={{ padding:4 }}><input value={r.lunghezza} onChange={e=>aggiornaRiga(r.id,"lunghezza",e.target.value)} style={inp} placeholder="Es. 130"/></td>
                <td style={{ padding:4 }}><input value={r.collegamenti} onChange={e=>aggiornaRiga(r.id,"collegamenti",e.target.value)} style={inp}/></td>
                <td style={{ padding:4, textAlign:"center" }}><button onClick={()=>eliminaRiga(r.id)} style={{ background:"none", border:"none", cursor:"pointer", color:"#dc2626", padding:2 }}><Trash2 size={13}/></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={aggiungiRiga} style={{ display:"flex", alignItems:"center", gap:6, fontSize:11.5, fontWeight:700, color:"#7c3aed", background:"none", border:"none", cursor:"pointer", marginBottom:16 }}>
        <Plus size={13}/> Aggiungi collegamento
      </button>

      {righeSenzaFormazione.length > 0 && (
        <div style={{ display:"flex", gap:8, background:"#fffbeb", border:"1px solid #fde68a", borderRadius:8, padding:"10px 12px", marginBottom:16, fontSize:11.5, color:"#92400e" }}>
          <AlertTriangle size={15} style={{ flexShrink:0, marginTop:1 }}/>
          <div>{righeSenzaFormazione.length} collegament{righeSenzaFormazione.length>1?"i":"o"} con lunghezza inserita ma senza formazione selezionata — non {righeSenzaFormazione.length>1?"entrano":"entra"} nella distinta.</div>
        </div>
      )}

      <div style={{ fontSize:13, fontWeight:700, color:"#111827", marginBottom:8 }}>3 · Distinta per sezione — cosa ordinare</div>
      {distinta.length === 0 ? (
        <div style={{ textAlign:"center", padding:24, color:"#9ca3af", fontSize:12 }}>Definisci una formazione e collegala a un collegamento per vedere la distinta.</div>
      ) : (
        <div style={{ border:"1px solid #e5e7eb", borderRadius:8, marginBottom:16 }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12.5 }}>
            <thead>
              <tr style={{ background:"#faf5ff" }}>
                <th style={{ padding:"8px 12px", textAlign:"left", color:"#6d28d9" }}>Sezione (mm²)</th>
                <th style={{ padding:"8px 12px", textAlign:"left", color:"#6d28d9" }}>Etichetta</th>
                <th style={{ padding:"8px 12px", textAlign:"right", color:"#6d28d9" }}>Metri da ordinare</th>
              </tr>
            </thead>
            <tbody>
              {distinta.map((d,i) => (
                <tr key={i} style={{ borderTop:"1px solid #f3f4f6" }}>
                  <td style={{ padding:"8px 12px", fontWeight:700 }}>{d.sezione}</td>
                  <td style={{ padding:"8px 12px", color:"#6b7280" }}>{d.etichetta||"—"}</td>
                  <td style={{ padding:"8px 12px", textAlign:"right", fontWeight:700, color:"#6d28d9" }}>{d.metri.toLocaleString("it-IT",{maximumFractionDigits:1})} m</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", borderTop:"1px solid #f3f4f6", paddingTop:14 }}>
        <button onClick={azzeraTutto} style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, color:"#9ca3af", background:"none", border:"none", cursor:"pointer" }}>
          <RefreshCw size={12}/> Azzera tutto
        </button>
        <button onClick={stampaPDF} disabled={distinta.length===0}
          title={distinta.length===0 ? "Serve almeno una riga nella distinta per stampare" : "Stampa PDF"}
          style={{ display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:700,
            color: distinta.length>0 ? "#7c3aed" : "#d1d5db",
            background: distinta.length>0 ? "#faf5ff" : "#f9fafb",
            border:`1px solid ${distinta.length>0?"#e9d5ff":"#e5e7eb"}`, borderRadius:8, padding:"9px 18px",
            cursor: distinta.length>0 ? "pointer" : "not-allowed" }}>
          <Printer size={13}/> Stampa PDF
        </button>
      </div>
    </div>
  );
}
